import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog } from '@components';
import dayjs from 'dayjs';
import styles from './DateTimePickerWrapper.styles';
import WheelColumn from './WheelColumn';
import { capitalize } from '../../lib/generique';

interface DateTimePickerEvent {
  type: string;
  nativeEvent: {
    timestamp?: number;
  };
}

interface DateTimePickerProps {
  value: Date;
  onChange?: (event: DateTimePickerEvent, date?: Date) => void;
  mode?: 'date' | 'time' | 'datetime';
  is24Hour?: boolean;
  display?: 'default' | 'spinner' | 'calendar' | 'clock';
  minimumDate?: Date;
  maximumDate?: Date;
}

const DateTimePicker: FunctionComponent<DateTimePickerProps> = ({
  value,
  onChange,
  mode = 'date',
  minimumDate,
  maximumDate,
}) => {
  const initial = useMemo(() => dayjs(value), [value]);
  const [day, setDay] = useState(initial.date() - 1);
  const [month, setMonth] = useState(initial.month());
  const [year, setYear] = useState(initial.year() - 1950);
  const [hour, setHour] = useState(initial.hour());
  const [minute, setMinute] = useState(initial.minute());

  const days = useMemo(() => Array.from({ length: dayjs().month(month).daysInMonth() }, (_, i) => i + 1), [month]);
  const months = useMemo(() => Array.from({ length: 12 }, (_, i) => capitalize(dayjs().month(i).format("MMMM"))), []);
  const years = useMemo(() => Array.from({ length: 101 }, (_, i) => 1950 + i),[]);
  const hours = useMemo(() => Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0')), []);
  const minutes = useMemo(() => Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0')), []);

  const title = useMemo(() => {switch (mode) {
    case 'date':
    case 'datetime':
      return "Définir la date";
    case 'time':
      return "Définir l'heure";
    default:
      break;}
  }, [mode])

  const handleValidate = useCallback(() => {
    const newDayjsDate = dayjs().year(years[year]).month(month).date(day + 1).hour(hour).minute(minute)
    if(!((minimumDate && newDayjsDate.isBefore(minimumDate)) || (maximumDate && newDayjsDate.isAfter(maximumDate)))){
      const newDate = newDayjsDate.toDate();
      onChange?.({type: 'set', nativeEvent: { timestamp: newDate.getTime() }}, newDate)
    }
  }, [day, hour, maximumDate, minimumDate, minute, month, onChange, year, years] );

  useEffect(() => {
    if (day >= days.length) {
      setDay(days.length - 1);
    }
  }, [day, days.length]);

  useEffect(() => {
    setHour(value.getHours());
    setMinute(value.getMinutes());
  }, [value]);

  const handleDismiss = useCallback(() => {
    onChange?.(
      {
        type: 'dismissed',
        nativeEvent: {},
      },
      undefined
    );
  }, [onChange]);


  return (
    <Dialog visible title={title} showCloseButton onClose={handleDismiss}>
      <div style={styles.picker}>
        {mode.includes('date') && (
          <div style={styles.timeRow}>
            <div style={styles.wheelSlot}>
              <div style={styles.wheels}>
                <div style={styles.selection} />
                <WheelColumn values={days} index={day} onChange={setDay} />
              </div>
            </div>

            <div style={styles.wheelSlot}>
              <div style={styles.wheels}>
                <div style={styles.selection} />
                <WheelColumn values={months} index={month} onChange={setMonth} />
              </div>
            </div>

            <div style={styles.wheelSlot}>
              <div style={styles.wheels}>
                <div style={styles.selection} />
                <WheelColumn values={years} index={year} onChange={setYear} />
              </div>
            </div>
          </div>
        )}
        {mode.includes('time') && (
          <div style={styles.timeRow}>
            <div style={styles.wheelSlot}>
              <div style={styles.wheels}>
                <div style={styles.selection} />
                <WheelColumn values={hours} index={hour} onChange={setHour} />
              </div>
            </div>

            <span style={styles.separator}>:</span>

            <div style={styles.wheelSlot}>
              <div style={styles.wheels}>
                <div style={styles.selection} />
                <WheelColumn values={minutes} index={minute} onChange={setMinute} />
              </div>
            </div>
          </div>
        )}

        <div style={styles.footer}>
          <button type="button" onClick={handleValidate} style={styles.button}>
            Valider
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export { DateTimePicker };
export default DateTimePicker;
